/**
 * DWG 上傳表單（Sprint 0 抽出 component，便於 S1+ 重用）
 * 純展示：選檔（拖拉 / 點選，accept .dwg,.dxf）→ 呼叫 onUpload(file)。
 */
import React, { useRef, useState } from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useTranslation } from 'react-i18next';

interface DwgUploadFormProps {
  onUpload: (file: File) => void;
  uploading: boolean;
}

const ACCEPT = '.dwg,.dxf';

const DwgUploadForm: React.FC<DwgUploadFormProps> = ({ onUpload, uploading }) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const pick = (f: File | null | undefined) => {
    if (f) setFile(f);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    pick(e.dataTransfer.files?.[0]);
  };

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Box
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        sx={{
          border: '2px dashed',
          borderColor: dragOver ? 'primary.main' : 'divider',
          borderRadius: 1,
          p: 4,
          textAlign: 'center',
          cursor: 'pointer',
          bgcolor: dragOver ? 'action.hover' : 'transparent',
        }}
      >
        <CloudUploadIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
        <Typography sx={{ mt: 1 }} color="text.secondary">
          {t('interiorDesign.uploadHint')}
        </Typography>
        {file && (
          <Typography sx={{ mt: 1 }} fontWeight={600}>
            {file.name}
          </Typography>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          hidden
          onChange={(e) => pick(e.target.files?.[0])}
        />
      </Box>
      <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
        <Button variant="outlined" onClick={() => inputRef.current?.click()} disabled={uploading}>
          {t('interiorDesign.selectFile')}
        </Button>
        <Button
          variant="contained"
          startIcon={<CloudUploadIcon />}
          disabled={!file || uploading}
          onClick={() => file && onUpload(file)}
        >
          {uploading ? t('interiorDesign.parsing') : t('interiorDesign.upload')}
        </Button>
      </Box>
    </Paper>
  );
};

export default DwgUploadForm;
